import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { ipdApi } from "@/api/ipd";
import { pharmacyApi } from "@/api/pharmacy";
import { toast } from "sonner";
import { Download, Loader2, PackageCheck, Plus, Trash2, Undo2, Upload } from "lucide-react";

type Drug = { id: string; name: string; strength?: string; form?: string; unit?: string };

export default function OTConsumptionEntryPage() {
  const qc = useQueryClient();
  const [admissionId, setAdmissionId] = useState("");
  const [surgeryDate, setSurgeryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [procedureName, setProcedureName] = useState("");
  const [surgeonId, setSurgeonId] = useState("");
  const [notes, setNotes] = useState("");
  const AUTO_BATCH = "__AUTO_BATCH__";
  const [returnQty, setReturnQty] = useState<Record<string, string>>({});
  const [attachment, setAttachment] = useState<File | null>(null);
  const [reportFrom, setReportFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportTo, setReportTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [usedRows, setUsedRows] = useState<Array<{ drugId: string; qty: string }>>([]);
  const [usedDrugSearch, setUsedDrugSearch] = useState("");

  const [drugSearch, setDrugSearch] = useState("");
  const [items, setItems] = useState<Array<{ drugId: string; qty: string; batchId?: string }>>([]);
  const [batchOptionsByDrug, setBatchOptionsByDrug] = useState<Record<string, any[]>>({});
  const kitQuery = useQuery({
    queryKey: ["ot-surgery-kit-admission", admissionId, surgeryDate],
    enabled: Boolean(admissionId && surgeryDate),
    queryFn: async () => {
      const res = await pharmacyApi.getOTSurgeryKitByAdmission({ admissionId, surgeryDate });
      return (res.data as { data?: any | null })?.data ?? null;
    },
  });

  const kit: any | null =
    !admissionId || !surgeryDate ? null : (kitQuery.data as any) ?? null;
  const kitLoading = Boolean(admissionId && surgeryDate && kitQuery.isPending);
  const kitIsOpen = !!kit?.id && String(kit.status ?? "").toUpperCase() === "OPEN";

  useEffect(() => {
    if (!admissionId || !surgeryDate) return;
    if (kitQuery.isPending) return;
    if (kit?.id) {
      setProcedureName(typeof kit.procedureName === "string" ? kit.procedureName : "");
      setSurgeonId(kit.surgeonId ?? "");
      setNotes(typeof kit.notes === "string" ? kit.notes : "");
    } else {
      setProcedureName("");
      setSurgeonId("");
      setNotes("");
    }
  }, [admissionId, surgeryDate, kitQuery.isPending, kit?.id, kit?.procedureName, kit?.surgeonId, kit?.notes]);

  useEffect(() => {
    setItems([]);
  }, [admissionId, surgeryDate]);

  const { data: admissions = [] } = useQuery({
    queryKey: ["ipd-admissions-all"],
    queryFn: async () => {
      // Include discharged admissions too (OT entry may be done after discharge).
      const res = await ipdApi.getAdmissions();
      return (res.data as any)?.data ?? [];
    },
  });

  const { data: doctors = [] } = useQuery({
    queryKey: ["doctors"],
    queryFn: async () => {
      const res = await api.get("/api/doctor/get-by-hospital");
      return (res.data as any)?.data ?? [];
    },
  });

  const { data: drugs = [] } = useQuery({
    queryKey: ["drugs-list"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  const filteredDrugs = useMemo(() => {
    const q = drugSearch.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => d.name.toLowerCase().includes(q) || (d.strength ?? "").toLowerCase().includes(q));
  }, [drugs, drugSearch]);

  const usedFilteredDrugs = useMemo(() => {
    const q = usedDrugSearch.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => d.name.toLowerCase().includes(q) || (d.strength ?? "").toLowerCase().includes(q));
  }, [drugs, usedDrugSearch]);

  // admissions are selected from dropdown; search can be added later if needed

  const addItem = async (drugId: string) => {
    setItems((prev) => [...prev, { drugId, qty: "1" }]);
    // preload OT batches (optional selection)
    const res = await api.get(`/api/pharmacy/drugs/${drugId}/batches`, { params: { all: true, location: "MAIN" } });
    const list = (res.data as any)?.data ?? [];
    setBatchOptionsByDrug((m) => ({ ...m, [drugId]: list }));
  };

  const issueMutation = useMutation({
    mutationFn: async () => {
      if (!admissionId) throw new Error("Select an admission");
      if (!surgeryDate) throw new Error("Select surgery date");
      if (items.length === 0) throw new Error("Add at least one drug");
      return pharmacyApi.issueOTSurgeryKit({
        admissionId,
        surgeryDate,
        procedureName: procedureName.trim() || undefined,
        surgeonId: surgeonId || undefined,
        notes: notes.trim() || undefined,
        items: items.map((i) => ({
          drugId: i.drugId,
          batchId: i.batchId && i.batchId !== AUTO_BATCH ? i.batchId : undefined,
          quantity: parseInt(i.qty, 10),
        })),
      });
    },
    onSuccess: async () => {
      setItems([]);
      toast.success("Medicines issued. Record returns after surgery, then finalize to bill net.");
      await qc.invalidateQueries({ queryKey: ["ot-dashboard"] });
      await qc.invalidateQueries({ queryKey: ["ot-surgery-kit-admission", admissionId, surgeryDate] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed"),
  });

  // Prefill "Actual used" with current net by drug when kit is created/updated
  useEffect(() => {
    if (!kit?.id) {
      setUsedRows([]);
      return;
    }
    const agg: Record<string, number> = {};
    for (const it of kit.items ?? []) {
      const net = Math.max(0, (it.issuedQty ?? 0) - (it.returnedQty ?? 0));
      if (!it.drugId || net <= 0) continue;
      agg[it.drugId] = (agg[it.drugId] ?? 0) + net;
    }
    const rows = Object.entries(agg).map(([drugId, qty]) => ({ drugId, qty: String(qty) }));
    setUsedRows(rows);
  }, [kit?.id, kit?.items]);

  const returnMutation = useMutation({
    mutationFn: async () => {
      if (!kit?.id) throw new Error("Issue medicines first");
      const returns = (kit.items ?? [])
        .map((it: any) => ({ itemId: it.id, quantity: Math.max(0, parseInt(returnQty[it.id] ?? "0", 10) || 0) }))
        .filter((r: any) => r.quantity > 0);
      if (returns.length === 0) throw new Error("Enter at least one return quantity");
      return pharmacyApi.returnOTSurgeryKit(kit.id, { returns });
    },
    onSuccess: async () => {
      setReturnQty({});
      toast.success("Return recorded");
      await qc.invalidateQueries({ queryKey: ["ot-dashboard"] });
      await qc.invalidateQueries({ queryKey: ["ot-surgery-kit-admission", admissionId, surgeryDate] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed"),
  });

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      if (!kit?.id) throw new Error("Issue medicines first");
      if (usedRows.length === 0) return pharmacyApi.finalizeOTSurgeryKit(kit.id);
      const actuals = usedRows
        .map((r) => ({ drugId: r.drugId, quantity: Math.floor(Number(r.qty)) }))
        .filter((x) => x.drugId && x.quantity > 0);
      if (actuals.length === 0) return pharmacyApi.finalizeOTSurgeryKit(kit.id);
      return pharmacyApi.finalizeOTSurgeryKitWithActuals(kit.id, { actuals });
    },
    onSuccess: async () => {
      toast.success("Finalized: net medicines billed to IPD");
      await qc.invalidateQueries({ queryKey: ["ot-dashboard"] });
      await qc.invalidateQueries({ queryKey: ["ot-surgery-kit-admission", admissionId, surgeryDate] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed"),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!kit?.id) throw new Error("Issue medicines first");
      if (!attachment) throw new Error("Select a PDF/image first");
      return pharmacyApi.uploadOTSurgeryKitAttachment(kit.id, attachment);
    },
    onSuccess: () => {
      setAttachment(null);
      toast.success("Attachment uploaded");
      void qc.invalidateQueries({ queryKey: ["ot-surgery-kit-admission", admissionId, surgeryDate] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed"),
  });

  const downloadCsv = async () => {
    if (!kit?.id) return;
    try {
      const res = await pharmacyApi.exportOTSurgeryKitCsv(kit.id);
      const blob = res.data as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ot-surgery-${kit.id}-${String(surgeryDate).slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to download CSV");
    }
  };

  const downloadRangeCsv = async () => {
    try {
      if (!reportFrom || !reportTo) throw new Error("Select from/to dates");
      const res = await pharmacyApi.exportOTSurgeryKitsCsvRange({ from: reportFrom, to: reportTo });
      const blob = res.data as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ot-surgeries-${reportFrom}-to-${reportTo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to download CSV");
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>OT Consumption Entry (Patient-linked)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col md:flex-row md:items-end gap-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 flex-1">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Report from</label>
                <Input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Report to</label>
                <Input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
              </div>
            </div>
            <Button variant="outline" onClick={downloadRangeCsv}>
              <Download className="h-4 w-4 mr-2" /> Download surgeries CSV
            </Button>
          </div>

          {kitLoading ? (
            <div className="text-xs text-muted-foreground">Loading OT surgery kit for this admission…</div>
          ) : null}
          {kit?.id ? (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Kit</span>: {kit.id} · status {kit.status}
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Admission *</label>
              <Select value={admissionId} onValueChange={setAdmissionId} disabled={!!kit?.id}>
                <SelectTrigger><SelectValue placeholder="Select admission" /></SelectTrigger>
                <SelectContent>
                  {(admissions as any[]).slice(0, 200).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.queue?.patient?.name} ({a.queue?.patient?.uhid ?? a.queue?.patient?.patientUniqueId ?? "—"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Surgery date *</label>
              <Input type="date" value={surgeryDate} onChange={(e) => setSurgeryDate(e.target.value)} disabled={!!kit?.id} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Surgeon</label>
              <Select value={surgeonId} onValueChange={setSurgeonId} disabled={!!kit?.id}>
                <SelectTrigger><SelectValue placeholder="Select surgeon (optional)" /></SelectTrigger>
                <SelectContent>
                  {(doctors as any[]).map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Procedure</label>
              <Input value={procedureName} onChange={(e) => setProcedureName(e.target.value)} placeholder="e.g. ORIF, LSCS" disabled={!!kit?.id} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes from OT sheet" disabled={!!kit?.id} />
            </div>
          </div>

          {kit?.id && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="md:col-span-2 space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Upload OT sheet (PDF/image)</label>
                <Input type="file" accept=".pdf,image/*" onChange={(e) => setAttachment(e.target.files?.[0] ?? null)} />
              </div>
              <div className="flex items-end gap-2">
                <Button variant="outline" onClick={() => uploadMutation.mutate()} disabled={uploadMutation.isPending || !kitIsOpen}>
                  <Upload className="h-4 w-4 mr-2" /> Upload
                </Button>
                <Button variant="outline" onClick={downloadCsv}>
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Drugs used</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Search drug to add..." value={drugSearch} onChange={(e) => setDrugSearch(e.target.value)} disabled={!!kit?.id} />
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-md p-2 bg-muted/30">
            {filteredDrugs.slice(0, 50).map((d) => (
              <Button key={d.id} size="sm" variant="outline" onClick={() => addItem(d.id)} disabled={!!kit?.id}>
                <Plus className="h-4 w-4 mr-2" />
                {d.name} {d.strength ? `(${d.strength})` : ""}
              </Button>
            ))}
          </div>

          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left p-2">Drug</th>
                  <th className="text-left p-2">Batch (optional)</th>
                  <th className="text-right p-2">Qty</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {kit?.id ? (
                  <tr>
                    <td colSpan={4} className="p-3 text-muted-foreground">
                      Already issued for this admission and surgery date — see{' '}
                      <span className="font-medium text-foreground">Issued vs returned</span> below. To add
                      more lines, use a new surgery date if appropriate, or contact admin.
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={4} className="p-3 text-muted-foreground">No items added</td></tr>
                ) : (
                  items.map((it, idx) => {
                    const drug = drugs.find((d) => d.id === it.drugId);
                    const opts = batchOptionsByDrug[it.drugId] ?? [];
                    return (
                      <tr key={`${it.drugId}-${idx}`} className="border-t">
                        <td className="p-2">{drug?.name} {drug?.strength ? `(${drug.strength})` : ""}</td>
                        <td className="p-2">
                          <Select
                            value={it.batchId ?? AUTO_BATCH}
                            onValueChange={(v) =>
                              setItems((prev) =>
                                prev.map((p, i) => (i === idx ? { ...p, batchId: v === AUTO_BATCH ? undefined : v } : p))
                              )
                            }
                          >
                            <SelectTrigger><SelectValue placeholder="FIFO (auto)" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value={AUTO_BATCH}>FIFO (auto)</SelectItem>
                              {opts.map((b: any) => (
                                <SelectItem key={b.batchId} value={b.batchId}>
                                  {b.batchNo} · exp {String(b.expiryDate).slice(0, 10)} · avail {b.available}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            min={1}
                            step={1}
                            value={it.qty}
                            onChange={(e) => setItems((prev) => prev.map((p, i) => (i === idx ? { ...p, qty: e.target.value } : p)))}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <Button variant="outline" size="sm" onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            {!kit?.id ? (
              <Button
                onClick={() => issueMutation.mutate()}
                disabled={issueMutation.isPending || kitLoading || !!kit?.id}
              >
                {issueMutation.isPending ? (
                  <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Issuing...</span>
                ) : (
                  "Issue medicines"
                )}
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => returnMutation.mutate()} disabled={returnMutation.isPending}>
                  {returnMutation.isPending ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Saving...</span>
                  ) : (
                    <>
                      <Undo2 className="h-4 w-4 mr-2" /> Record return
                    </>
                  )}
                </Button>
                <Button onClick={() => finalizeMutation.mutate()} disabled={finalizeMutation.isPending || !kitIsOpen}>
                  {finalizeMutation.isPending ? (
                    <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Finalizing...</span>
                  ) : (
                    <>
                      <PackageCheck className="h-4 w-4 mr-2" /> Finalize (bill net)
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>

          {kit?.id && (
            <div className="border rounded-md overflow-hidden">
              <div className="p-2 text-sm font-medium bg-muted/30">Issued vs returned (net is billed)</div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2">Drug</th>
                    <th className="text-left p-2">Batch</th>
                    <th className="text-right p-2">Issued</th>
                    <th className="text-right p-2">Returned</th>
                    <th className="text-right p-2">Return now</th>
                    <th className="text-right p-2">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {(kit.items ?? []).length === 0 ? (
                    <tr><td colSpan={6} className="p-3 text-muted-foreground">No issued items</td></tr>
                  ) : (
                    (kit.items ?? []).map((it: any) => {
                      const net = Math.max(0, (it.issuedQty ?? 0) - (it.returnedQty ?? 0));
                      return (
                        <tr key={it.id} className="border-t">
                          <td className="p-2">{it.drug?.name} {it.drug?.strength ? `(${it.drug.strength})` : ""}</td>
                          <td className="p-2 font-mono">{it.batch?.batchNo ?? "—"}</td>
                          <td className="p-2 text-right">{it.issuedQty}</td>
                          <td className="p-2 text-right">{it.returnedQty}</td>
                          <td className="p-2 text-right">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={returnQty[it.id] ?? ""}
                              onChange={(e) => setReturnQty((p) => ({ ...p, [it.id]: e.target.value }))}
                              placeholder="0"
                              disabled={!kitIsOpen}
                            />
                          </td>
                          <td className="p-2 text-right font-medium">{net}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {kit?.id && (
            <div className="border rounded-md overflow-hidden">
              <div className="p-2 text-sm font-medium bg-muted/30">
                Actual used (from OT paper) — can include extra medicines (deducted from OT stock)
              </div>
              <div className="p-2 flex flex-col gap-2">
                <Input
                  placeholder="Search drug to add to used list..."
                  value={usedDrugSearch}
                  onChange={(e) => setUsedDrugSearch(e.target.value)}
                  disabled={!kitIsOpen}
                />
                <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto border rounded-md p-2 bg-muted/10">
                  {usedFilteredDrugs.slice(0, 30).map((d) => (
                    <Button
                      key={`used-${d.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setUsedRows((prev) => (prev.some((x) => x.drugId === d.id) ? prev : [...prev, { drugId: d.id, qty: "1" }]))
                      }
                      disabled={!kitIsOpen}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {d.name} {d.strength ? `(${d.strength})` : ""}
                    </Button>
                  ))}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left p-2">Drug</th>
                    <th className="text-right p-2">Used qty</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {usedRows.length === 0 ? (
                    <tr><td colSpan={3} className="p-3 text-muted-foreground">No used items</td></tr>
                  ) : (
                    usedRows.map((r, idx) => {
                      const drug = drugs.find((d) => d.id === r.drugId);
                      return (
                        <tr key={`${r.drugId}-${idx}`} className="border-t">
                          <td className="p-2">{drug?.name} {drug?.strength ? `(${drug.strength})` : ""}</td>
                          <td className="p-2 text-right">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={r.qty}
                              onChange={(e) => setUsedRows((p) => p.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))}
                              disabled={!kitIsOpen}
                            />
                          </td>
                          <td className="p-2 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setUsedRows((p) => p.filter((_, i) => i !== idx))}
                              disabled={!kitIsOpen}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

